import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Download } from 'lucide-react';
import { adminApi } from '../services/adminApi';
import { StatsCard } from '../components/StatsCard';

const thisMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const n = (value) => value.toLocaleString('uk-UA');
const money = (value) => value.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// The month a postpaid workspace is invoiced for: every document with its charge, the totals in USD and in
// hryvnias at the NBU rate, the spreadsheet to attach to the invoice, and "do not invoice" per document.
export function WorkspaceReportPage() {
  const { workspaceId } = useParams();
  const [month, setMonth] = useState(thisMonth());
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [busyRow, setBusyRow] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setReport(await adminApi.getWorkspaceReport(workspaceId, month));
    } catch (e) {
      setReport(null);
      setError(e.response?.data?.detail || 'Failed to load the report');
    }
  }, [workspaceId, month]);

  useEffect(() => {
    load();
  }, [load]);

  const downloadCsv = async () => {
    const response = await adminApi.downloadWorkspaceReportCsv(workspaceId, month);
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${report.workspace_name} ${month}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const doNotInvoice = async (row) => {
    const reason = window.prompt(
      `Do not invoice "${row.filename || 'deleted document'}" (${n(row.billed_chars)} chars)? Reason:`,
      'layout unusable after redo',
    );
    if (reason === null) return;
    setBusyRow(row.transaction_id);
    try {
      await adminApi.refundTransaction(row.transaction_id, `not invoiced: ${reason}`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to mark the document');
    } finally {
      setBusyRow(null);
    }
  };

  return (
    <div>
      <Link to="/admin/workspaces" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
        <ArrowLeft size={16} /> Workspaces
      </Link>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>
          {report ? report.workspace_name : 'Month report'}
        </h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="month"
            className="admin-input"
            value={month}
            max={thisMonth()}
            onChange={(e) => e.target.value && setMonth(e.target.value)}
            data-testid="report-month"
          />
          <button className="admin-btn admin-btn-secondary" onClick={downloadCsv} disabled={!report}>
            <Download size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
            CSV
          </button>
        </div>
      </div>

      {error && <div className="admin-card" style={{ color: 'var(--danger)' }}>{error}</div>}

      {report && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
            <StatsCard title="Documents" value={n(report.totals.documents)} />
            <StatsCard title="Chars billed" value={n(report.totals.billed_chars)} />
            <StatsCard title="Of them free" value={n(report.totals.free_chars)} />
            <StatsCard title="Chars invoiced" value={n(report.totals.invoiced_chars)} />
            <StatsCard title="Invoice, $" value={money(report.usd)} />
            <StatsCard title="Invoice, ₴" value={money(report.uah)} />
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: -8, marginBottom: 16 }} data-testid="report-rate">
            NBU rate {report.uah_per_usd.toFixed(4)} ₴/$ on {new Date(report.rate_date).toLocaleDateString('uk-UA')}
          </p>

          <div className="admin-card" style={{ padding: 0, overflowX: 'auto' }}>
            <table className="admin-table" data-testid="report-rows">
              <thead>
                <tr>
                  <th>Date (Kyiv)</th>
                  <th>Document</th>
                  <th>User</th>
                  <th>Service</th>
                  <th style={{ textAlign: 'right' }}>Pages</th>
                  <th style={{ textAlign: 'right' }}>Read</th>
                  <th style={{ textAlign: 'right' }}>Billed</th>
                  <th style={{ textAlign: 'right' }}>Free</th>
                  <th style={{ textAlign: 'right' }}>Invoiced</th>
                  <th style={{ textAlign: 'right' }}>$ / 1000</th>
                  <th style={{ textAlign: 'right' }}>$</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {report.rows.length === 0 ? (
                  <tr>
                    <td colSpan={12} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                      No documents this month
                    </td>
                  </tr>
                ) : report.rows.map((row) => (
                  <tr key={row.transaction_id} style={row.charged ? undefined : { color: 'var(--text-muted)' }}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {new Date(row.created_at).toLocaleString('uk-UA', { timeZone: 'Europe/Kyiv', dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                    <td>{row.filename || <em>deleted</em>}</td>
                    <td style={{ fontSize: 13 }}>{row.user_email}</td>
                    <td>{row.service || '-'}</td>
                    <td style={{ textAlign: 'right' }}>{row.pages ?? '-'}</td>
                    <td style={{ textAlign: 'right' }}>{row.chars_read != null ? n(row.chars_read) : '-'}</td>
                    <td style={{ textAlign: 'right' }}>{n(row.billed_chars)}</td>
                    <td style={{ textAlign: 'right' }}>{n(row.free_chars)}</td>
                    <td style={{ textAlign: 'right' }}>{n(row.invoiced_chars)}</td>
                    <td style={{ textAlign: 'right' }}>{row.usd_per_1k != null ? row.usd_per_1k.toFixed(2) : '-'}</td>
                    <td style={{ textAlign: 'right' }}>{money(row.usd)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {row.charged ? (
                        <button
                          className="admin-btn admin-btn-secondary"
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={() => doNotInvoice(row)}
                          disabled={busyRow === row.transaction_id}
                        >
                          Do not invoice
                        </button>
                      ) : (
                        <span className="admin-badge admin-badge-warning">not invoiced</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
